/**
 * OrderPlacementModal — back-office rep confirms vendor, line items, prices,
 * substitutions, payment terms, and marks the packet placed/received.
 *
 * Scope:
 *   • Editable per line: actual SKU, vendor, qty, unit price, tax, shipping,
 *     fees, status, expected delivery date, vendor order #, notes
 *   • Substitution requires reason
 *   • Vendor change requires reason
 *   • Price overrides (when actual differs from projected by >10% OR rep
 *     marks "override") require reason
 *   • Mark Placed → status flips to placed/partially_placed
 *   • Mark Received → status flips to received
 *   • Cancel → typed reason
 *   • Full audit trail visible
 *
 * Pricing edits are allowed at ANY time after placement — every revision is
 * logged in the audit trail. Actuals become the COGS source for accounting.
 */
import { useMemo, useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Package, CheckCircle2, AlertTriangle, History, Truck, X, ArrowLeftRight,
  DollarSign, Calendar, Save, Send, Ban, Receipt as ReceiptIcon, FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import {
  PACKET_STATUS_LABEL, PACKET_STATUS_TONE, LINE_STATUS_LABEL,
  type OrderingLineItem, type OrderingPacket, type OrderingLineStatus,
} from "@/data/orderingPacket/types";
import { ReceiptsPane } from "./ReceiptsPane";
import { POSummaryPane } from "./POSummaryPane";
import { format } from "date-fns";

interface OrderPlacementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packetId: string | null;
  /**
   * Push-mode hook: fired right after the rep marks the packet placed.
   * Lets the entry-point surface (e.g. Job Detail) show a soft scheduling CTA
   * without the modal itself knowing about scheduling rules.
   */
  onPlaced?: (packetId: string) => void;
}

const TONE_BADGE: Record<string, string> = {
  muted: "bg-muted text-muted-foreground",
  info: "bg-info/15 text-info border-info/30",
  warning: "bg-warning/15 text-warning border-warning/30",
  success: "bg-success/15 text-success border-success/30",
  destructive: "bg-destructive/15 text-destructive border-destructive/30",
};

export function OrderPlacementModal({ open, onOpenChange, packetId, onPlaced }: OrderPlacementModalProps) {
  const {
    getPacket, vendorLeadTimes, updateLineItem, setLineStatus,
    updatePacketMeta, markPlaced, markReceived, cancelPacket, addLineException,
  } = useOrderingPacket();

  const packet = packetId ? getPacket(packetId) : undefined;
  const showMissingPacket = open && !!packetId && !packet;

  if (!open) return null;

  if (showMissingPacket) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Packet not found</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This ordering packet may have been removed.</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1500px] w-[97vw] h-[94vh] max-h-[94vh] p-0 gap-0 overflow-hidden min-h-0 !grid !grid-rows-[auto_minmax(0,1fr)_auto]">
        <PlacementHeader packet={packet} />
        <Tabs defaultValue="lines" className="min-h-0 overflow-hidden grid grid-rows-[auto_minmax(0,1fr)]">
          <TabsList className="mx-6 mt-3 self-start shrink-0">
            <TabsTrigger value="lines" className="text-xs">
              <Package className="h-3.5 w-3.5 mr-1.5" /> Line Items ({packet.lineItems.length})
            </TabsTrigger>
            <TabsTrigger value="pos" className="text-xs">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Purchase Orders ({(packet.purchaseOrders ?? []).length})
            </TabsTrigger>
            <TabsTrigger value="receipts" className="text-xs">
              <ReceiptIcon className="h-3.5 w-3.5 mr-1.5" /> Receipts ({(packet.receipts ?? []).length})
            </TabsTrigger>
            <TabsTrigger value="vendor" className="text-xs">
              <Truck className="h-3.5 w-3.5 mr-1.5" /> Vendor & Payment
            </TabsTrigger>
            <TabsTrigger value="audit" className="text-xs">
              <History className="h-3.5 w-3.5 mr-1.5" /> Audit Trail ({packet.auditEvents.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lines" className="h-full min-h-0 overflow-hidden m-0 px-6 pb-4 pt-3 data-[state=inactive]:hidden">
            <LineItemsPane
              packet={packet}
              onUpdate={(lineId, patch, opts) => updateLineItem(packet.id, lineId, patch, opts)}
              onSetStatus={(lineId, status, reason) => setLineStatus(packet.id, lineId, status, reason)}
              onAddException={(lineId, kind, detail) => addLineException(packet.id, lineId, kind, detail)}
            />
          </TabsContent>

          <TabsContent value="pos" className="h-full min-h-0 overflow-hidden m-0 px-6 pb-4 pt-3 data-[state=inactive]:hidden">
            <POSummaryPane packet={packet} />
          </TabsContent>

          <TabsContent value="receipts" className="h-full min-h-0 overflow-hidden m-0 px-6 pb-4 pt-3 data-[state=inactive]:hidden">
            <ReceiptsPane packet={packet} />
          </TabsContent>

          <TabsContent value="vendor" className="h-full min-h-0 overflow-y-auto m-0 px-6 pb-4 pt-3 data-[state=inactive]:hidden">
            <VendorPaymentPane
              packet={packet}
              vendorLeadTimes={vendorLeadTimes}
              onUpdate={(patch) => updatePacketMeta(packet.id, patch)}
            />
          </TabsContent>

          <TabsContent value="audit" className="h-full min-h-0 overflow-y-auto m-0 px-6 pb-4 pt-3 data-[state=inactive]:hidden">
            <AuditPane packet={packet} />
          </TabsContent>
        </Tabs>

        <PlacementFooter
          packet={packet}
          onMarkPlaced={() => { markPlaced(packet.id); onPlaced?.(packet.id); }}
          onMarkReceived={() => markReceived(packet.id)}
          onCancel={(reason) => cancelPacket(packet.id, reason)}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

/* ────────── HEADER ────────── */
function PlacementHeader({ packet }: { packet: OrderingPacket }) {
  const tone = PACKET_STATUS_TONE[packet.status];
  const variance = packet.actualTotal - packet.projectedTotal;
  return (
    <DialogHeader className="px-6 pt-5 pb-3 border-b border-border space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <DialogTitle className="text-[16px] font-bold flex items-center gap-2">
            <Package className="h-4.5 w-4.5 text-primary" />
            Order Placement — {packet.customerName}
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {packet.jobLabel} · Sent {format(new Date(packet.sentAt), "MMM d, h:mm a")} by {packet.sentBy}
          </p>
        </div>
        <Badge className={cn("text-[11px] font-semibold border", TONE_BADGE[tone])}>
          {PACKET_STATUS_LABEL[packet.status]}
        </Badge>
      </div>
      <div className="grid grid-cols-4 gap-3 text-[11px] pt-1">
        <Metric label="Intended Vendor" value={packet.intendedVendor} />
        <Metric label="Order Type" value={packet.orderType} tone={packet.orderType === "Rush" ? "destructive" : undefined} />
        <Metric label="Projected COGS" value={`$${packet.projectedTotal.toLocaleString()}`} />
        <Metric
          label="Actual COGS"
          value={packet.actualTotal > 0 ? `$${packet.actualTotal.toLocaleString()}` : "—"}
          sub={packet.actualTotal > 0 && variance !== 0
            ? `${variance > 0 ? "+" : ""}$${Math.abs(variance).toLocaleString()} vs projected`
            : undefined}
          tone={variance > 0 ? "warning" : variance < 0 ? "success" : undefined}
        />
      </div>
      {packet.exceptionCount > 0 && (
        <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-md bg-warning/10 border border-warning/30">
          <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
          <span className="text-[11px] font-semibold text-warning">
            {packet.exceptionCount} unresolved exception{packet.exceptionCount === 1 ? "" : "s"}
          </span>
          <span className="text-[11px] text-warning/80">— review line items below</span>
        </div>
      )}
    </DialogHeader>
  );
}

function Metric({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "warning" | "success" | "destructive" }) {
  const toneCls = tone === "warning" ? "text-warning" : tone === "success" ? "text-success" : tone === "destructive" ? "text-destructive" : "text-foreground";
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={cn("text-[12px] font-bold tabular-nums", toneCls)}>{value}</span>
      {sub && <span className="text-[10px] text-muted-foreground tabular-nums">{sub}</span>}
    </div>
  );
}

/* ────────── LINE ITEMS PANE ────────── */
function LineItemsPane({
  packet, onUpdate, onSetStatus, onAddException,
}: {
  packet: OrderingPacket;
  onUpdate: (lineId: string, patch: Partial<OrderingLineItem>, opts?: { reason?: string; isOverride?: boolean }) => void;
  onSetStatus: (lineId: string, status: OrderingLineStatus, reason?: string) => void;
  onAddException: (lineId: string, kind: import("@/data/orderingPacket/types").OrderingLineExceptionKind, detail: string) => void;
}) {
  const [editingLine, setEditingLine] = useState<string | null>(null);

  // Completeness rollup — checklist signal at a glance
  const counts = useMemo(() => {
    let ready = 0, incomplete = 0, ordered = 0, received = 0;
    packet.lineItems.forEach((l) => {
      const state = getLineState(l);
      if (state === "ready") ready++;
      else if (state === "incomplete") incomplete++;
      else if (state === "ordered") ordered++;
      else if (state === "received") received++;
    });
    return { ready, incomplete, ordered, received, total: packet.lineItems.length };
  }, [packet.lineItems]);

  const allDone = counts.ordered + counts.received === counts.total && counts.total > 0;

  return (
    <div className="h-full min-h-0 overflow-hidden grid grid-rows-[auto_minmax(0,1fr)_auto]">
      {/* Sticky completeness counter */}
      <div className={cn(
        "flex items-center gap-3 px-3 py-2 mb-2 rounded-md border text-[11px] font-medium shrink-0",
        allDone
          ? "bg-success/10 border-success/30 text-success"
          : counts.incomplete > 0
            ? "bg-warning/10 border-warning/30"
            : "bg-primary/10 border-primary/30",
      )}>
        <span className="font-semibold">
          {allDone
            ? `All ${counts.total} lines ordered — ready to mark placed`
            : `${counts.ordered + counts.received} of ${counts.total} ordered`}
        </span>
        {!allDone && (
          <>
            {counts.ready > 0 && (
              <span className="text-primary">· {counts.ready} ready to order</span>
            )}
            {counts.incomplete > 0 && (
              <span className="text-warning">· {counts.incomplete} need vendor or price</span>
            )}
          </>
        )}
      </div>

      <div className="min-h-0 overflow-y-auto overscroll-contain rounded-lg border border-border">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="text-[10px] uppercase tracking-wider w-[110px]">SKU</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Item</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider w-[140px]">Vendor</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right w-[60px]">Qty</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right w-[160px]">Unit Price</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right w-[100px]">Line Cost</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider w-[120px]">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider w-[110px]">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {packet.lineItems.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                isEditing={editingLine === line.id}
                onToggleEdit={() => setEditingLine(editingLine === line.id ? null : line.id)}
                onUpdate={(patch, opts) => onUpdate(line.id, patch, opts)}
                onSetStatus={(s, r) => onSetStatus(line.id, s, r)}
                onAddException={(k, d) => onAddException(line.id, k, d)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2 shrink-0">
        Click any vendor or price cell to edit inline. Click <strong>Edit</strong> for SKU swaps, tax, shipping, fees. Every change is logged.
      </div>
    </div>
  );
}

/* ────────── LINE COMPLETENESS STATE ────────── */
type LineState = "incomplete" | "ready" | "ordered" | "received";

function getLineState(line: OrderingLineItem): LineState {
  if (line.status === "received") return "received";
  if (line.status === "ordered") return "ordered";
  const vendor = line.actualVendor ?? line.projectedVendor;
  const price = line.actualUnitPrice ?? line.projectedUnitPrice;
  const qty = line.actualQty ?? line.projectedQty;
  const hasOpenExceptions = line.exceptions.some((e) => !e.resolved);
  const vendorOk = !!vendor && vendor !== "Unassigned";
  const priceOk = price > 0;
  const qtyOk = qty > 0;
  if (vendorOk && priceOk && qtyOk && !hasOpenExceptions) return "ready";
  return "incomplete";
}

const LINE_STATE_STYLES: Record<LineState, { row: string; border: string; pill: string; pillLabel: string }> = {
  incomplete: {
    row: "bg-warning/[0.04] hover:bg-warning/[0.06]",
    border: "border-l-2 border-l-warning/60",
    pill: "bg-warning/15 text-warning border-warning/30",
    pillLabel: "Needs info",
  },
  ready: {
    row: "bg-primary/[0.03] hover:bg-primary/[0.05]",
    border: "border-l-2 border-l-primary/50",
    pill: "bg-primary/15 text-primary border-primary/30",
    pillLabel: "Ready",
  },
  ordered: {
    row: "bg-success/[0.05] hover:bg-success/[0.08]",
    border: "border-l-2 border-l-success/60",
    pill: "bg-success/15 text-success border-success/30",
    pillLabel: "Ordered",
  },
  received: {
    row: "bg-success/[0.10] hover:bg-success/[0.13]",
    border: "border-l-2 border-l-success",
    pill: "bg-success/20 text-success border-success/40",
    pillLabel: "Received",
  },
};

function LineRow({
  line, isEditing, onToggleEdit, onUpdate, onSetStatus, onAddException,
}: {
  line: OrderingLineItem;
  isEditing: boolean;
  onToggleEdit: () => void;
  onUpdate: (patch: Partial<OrderingLineItem>, opts?: { reason?: string; isOverride?: boolean }) => void;
  onSetStatus: (s: OrderingLineStatus, reason?: string) => void;
  onAddException: (kind: import("@/data/orderingPacket/types").OrderingLineExceptionKind, detail: string) => void;
}) {
  const sku = line.actualSku ?? line.projectedSku;
  const item = line.actualItemName ?? line.projectedItemName;
  const qty = line.actualQty ?? line.projectedQty;
  const lineCost = line.actualLineCost ?? line.projectedLineCost;
  const variance = (line.actualUnitPrice ?? 0) - line.projectedUnitPrice;
  const hasOpenExceptions = line.exceptions.some((e) => !e.resolved);
  const state = getLineState(line);
  const styles = LINE_STATE_STYLES[state];
  const isReady = state === "ready";
  const isLocked = state === "ordered" || state === "received";

  return (
    <>
      <TableRow className={cn(styles.row, styles.border, isEditing && "ring-1 ring-primary/40")}>
        <TableCell className="text-[11px] font-mono py-2">
          <div className="flex items-center gap-1.5">
            {state === "received" && <CheckCircle2 className="h-3 w-3 text-success" />}
            {state === "ordered" && <CheckCircle2 className="h-3 w-3 text-success/70" />}
            <span>{sku}</span>
            {line.substituted && <ArrowLeftRight className="h-3 w-3 inline text-warning" />}
          </div>
        </TableCell>
        <TableCell className="text-[11px] py-2 font-medium">{item}</TableCell>
        <TableCell className="text-[11px] py-2 text-muted-foreground">
          <InlineTextCell
            value={line.actualVendor ?? line.projectedVendor}
            placeholder="Add vendor"
            isMissing={!line.actualVendor && (!line.projectedVendor || line.projectedVendor === "Unassigned")}
            onCommit={(v) => {
              const trimmed = v.trim();
              if (!trimmed || trimmed === (line.actualVendor ?? line.projectedVendor)) return;
              onUpdate({ actualVendor: trimmed });
            }}
          />
          {line.vendorChanged && <Badge variant="outline" className="ml-1 text-[9px] py-0 px-1">changed</Badge>}
        </TableCell>
        <TableCell className="text-[11px] py-2 text-right tabular-nums">{qty}</TableCell>
        <TableCell className="text-[11px] py-2 text-right tabular-nums">
          <InlineNumberCell
            value={line.actualUnitPrice ?? (line.projectedUnitPrice > 0 ? line.projectedUnitPrice : null)}
            placeholder="Add price"
            isMissing={(line.actualUnitPrice ?? line.projectedUnitPrice) <= 0}
            onCommit={(n) => {
              if (n <= 0) return;
              const current = line.actualUnitPrice ?? line.projectedUnitPrice;
              if (n === current) return;
              const projected = line.projectedUnitPrice;
              const variancePct = projected > 0 ? Math.abs((n - projected) / projected) * 100 : 0;
              onUpdate({ actualUnitPrice: n }, { isOverride: variancePct > 10 });
            }}
          />
          {variance !== 0 && line.actualUnitPrice != null && (
            <span className={cn("block text-[9px]", variance > 0 ? "text-warning" : "text-success")}>
              {variance > 0 ? "+" : ""}${Math.abs(variance).toFixed(0)} vs proj
            </span>
          )}
        </TableCell>
        <TableCell className="text-[11px] py-2 text-right font-semibold tabular-nums">
          {lineCost > 0 ? `$${lineCost.toLocaleString()}` : "—"}
        </TableCell>
        <TableCell className="py-2">
          <div className="flex items-center gap-1.5">
            <Badge className={cn("text-[9px] font-semibold border shrink-0", styles.pill)}>
              {styles.pillLabel}
            </Badge>
            <Select value={line.status} onValueChange={(v) => onSetStatus(v as OrderingLineStatus)}>
              <SelectTrigger className="h-6 text-[10px] px-1.5 w-[60px]">
                <span className="sr-only">Status</span>
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(LINE_STATUS_LABEL) as OrderingLineStatus[]).map((s) => (
                  <SelectItem key={s} value={s} className="text-[11px]">{LINE_STATUS_LABEL[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="flex items-center gap-1">
            {isReady && (
              <PermissionGate permission="ordering_create">
                <Button
                  size="sm"
                  className="h-7 text-[10px] gap-1 bg-success hover:bg-success/90 text-success-foreground px-2"
                  onClick={() => onSetStatus("ordered")}
                  title="Mark this line as placed with the vendor"
                >
                  <CheckCircle2 className="h-3 w-3" /> Order
                </Button>
              </PermissionGate>
            )}
            {!isReady && (
              <Button size="sm" variant={isEditing ? "secondary" : "outline"} className="h-7 text-[10px]" onClick={onToggleEdit}>
                {isEditing ? "Close" : isLocked ? "View" : "Edit"}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      {isEditing && (
        <TableRow className="bg-primary/[0.03]">
          <TableCell colSpan={8} className="py-3">
            <LineEditor line={line} onUpdate={onUpdate} onAddException={onAddException} />
          </TableCell>
        </TableRow>
      )}
      {hasOpenExceptions && !isEditing && (
        <TableRow className="bg-warning/[0.04]">
          <TableCell colSpan={8} className="py-2 px-4">
            <div className="space-y-1">
              {line.exceptions.filter((e) => !e.resolved).map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]">
                  <AlertTriangle className="h-3 w-3 text-warning shrink-0" />
                  <span className="font-semibold text-warning">{e.kind}:</span>
                  <span className="text-muted-foreground">{e.detail}</span>
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function LineEditor({
  line, onUpdate, onAddException,
}: {
  line: OrderingLineItem;
  onUpdate: (patch: Partial<OrderingLineItem>, opts?: { reason?: string; isOverride?: boolean }) => void;
  onAddException: (kind: import("@/data/orderingPacket/types").OrderingLineExceptionKind, detail: string) => void;
}) {
  const [draft, setDraft] = useState({
    actualSku: line.actualSku ?? line.projectedSku,
    actualItemName: line.actualItemName ?? line.projectedItemName,
    actualVendor: line.actualVendor ?? line.projectedVendor,
    actualQty: line.actualQty ?? line.projectedQty,
    actualUnitPrice: line.actualUnitPrice ?? line.projectedUnitPrice,
    actualTax: line.actualTax ?? 0,
    actualShipping: line.actualShipping ?? 0,
    actualFees: line.actualFees ?? 0,
    vendorOrderNumber: line.vendorOrderNumber ?? "",
    expectedDeliveryDate: line.expectedDeliveryDate ?? "",
    notes: line.notes ?? "",
  });
  const [reason, setReason] = useState("");

  const skuChanged = draft.actualSku !== line.projectedSku;
  const vendorChanged = draft.actualVendor !== line.projectedVendor;
  const priceChanged = draft.actualUnitPrice !== (line.actualUnitPrice ?? line.projectedUnitPrice);
  const projectedPrice = line.projectedUnitPrice;
  const variancePct = projectedPrice > 0
    ? Math.abs((draft.actualUnitPrice - projectedPrice) / projectedPrice) * 100
    : 0;
  const isLargeVariance = projectedPrice > 0 && variancePct > 10;
  const requiresReason = skuChanged || vendorChanged || isLargeVariance;
  const canSave = !requiresReason || reason.trim().length > 0;

  const handleSave = () => {
    onUpdate(draft, {
      reason: reason || undefined,
      isOverride: isLargeVariance,
    });
    setReason("");
  };

  return (
    <div className="grid grid-cols-12 gap-3 px-2">
      <Field label="SKU" className="col-span-2">
        <Input value={draft.actualSku} onChange={(e) => setDraft({ ...draft, actualSku: e.target.value })} className="h-8 text-[11px] font-mono" />
      </Field>
      <Field label="Item Name" className="col-span-3">
        <Input value={draft.actualItemName} onChange={(e) => setDraft({ ...draft, actualItemName: e.target.value })} className="h-8 text-[11px]" />
      </Field>
      <Field label="Vendor" className="col-span-2">
        <Input value={draft.actualVendor} onChange={(e) => setDraft({ ...draft, actualVendor: e.target.value })} className="h-8 text-[11px]" />
      </Field>
      <Field label="Qty" className="col-span-1">
        <Input type="number" value={draft.actualQty} onChange={(e) => setDraft({ ...draft, actualQty: Number(e.target.value) })} className="h-8 text-[11px] text-right" />
      </Field>
      <Field label="Unit Price" className="col-span-2">
        <Input type="number" step="0.01" value={draft.actualUnitPrice} onChange={(e) => setDraft({ ...draft, actualUnitPrice: Number(e.target.value) })} className="h-8 text-[11px] text-right" />
      </Field>
      <Field label="Tax" className="col-span-1">
        <Input type="number" step="0.01" value={draft.actualTax} onChange={(e) => setDraft({ ...draft, actualTax: Number(e.target.value) })} className="h-8 text-[11px] text-right" />
      </Field>
      <Field label="Ship" className="col-span-1">
        <Input type="number" step="0.01" value={draft.actualShipping} onChange={(e) => setDraft({ ...draft, actualShipping: Number(e.target.value) })} className="h-8 text-[11px] text-right" />
      </Field>

      <Field label="Vendor Order #" className="col-span-3">
        <Input value={draft.vendorOrderNumber} onChange={(e) => setDraft({ ...draft, vendorOrderNumber: e.target.value })} placeholder="e.g. SEN-12345" className="h-8 text-[11px]" />
      </Field>
      <Field label="Expected Delivery" className="col-span-3">
        <Input type="date" value={draft.expectedDeliveryDate?.slice(0, 10) ?? ""} onChange={(e) => setDraft({ ...draft, expectedDeliveryDate: e.target.value })} className="h-8 text-[11px]" />
      </Field>
      <Field label="Fees" className="col-span-2">
        <Input type="number" step="0.01" value={draft.actualFees} onChange={(e) => setDraft({ ...draft, actualFees: Number(e.target.value) })} className="h-8 text-[11px] text-right" />
      </Field>
      <Field label="Notes" className="col-span-4">
        <Input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Optional notes…" className="h-8 text-[11px]" />
      </Field>

      {requiresReason && (
        <div className="col-span-12 mt-1 p-3 rounded-md bg-warning/10 border border-warning/30 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-warning" />
            <span className="text-[11px] font-semibold text-warning">
              {skuChanged && "SKU substitution. "}
              {vendorChanged && "Vendor change. "}
              {isLargeVariance && `Price variance ${variancePct.toFixed(0)}% (>10%). `}
              Reason required.
            </span>
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain the change for leadership review…"
            className="text-[11px] min-h-[50px] bg-card"
          />
        </div>
      )}

      <div className="col-span-12 flex items-center justify-between pt-2 border-t border-border">
        <div className="text-[10px] text-muted-foreground">
          Projected: <span className="font-mono">{line.projectedSku}</span> · {line.projectedVendor} · ${line.projectedUnitPrice.toLocaleString()} × {line.projectedQty}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[10px] text-muted-foreground"
            onClick={() => onAddException("vendor_unavailable", "Vendor cannot fulfill — needs sourcing")}
          >
            Flag exception
          </Button>
          <PermissionGate permission="ordering_create">
            <Button size="sm" disabled={!canSave} onClick={handleSave} className="h-8 text-[11px] gap-1.5">
              <Save className="h-3 w-3" /> Save line
            </Button>
          </PermissionGate>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-1", className)}>
      <label className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

/* ────────── INLINE EDIT CELLS ──────────
 * Lightweight click-to-edit cells used inside the line-items table so reps
 * can patch missing vendor/price data without opening the full row editor.
 * Auto-opens when the value is missing (BOM gap) — that's the unblock path
 * for "no_price" exceptions. Commits on blur or Enter; Esc cancels.
 */
function InlineTextCell({
  value, placeholder, isMissing, onCommit,
}: { value: string; placeholder: string; isMissing: boolean; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(isMissing);
  const [draft, setDraft] = useState(isMissing ? "" : value);

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { onCommit(draft); if (draft.trim()) setEditing(false); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { onCommit(draft); if (draft.trim()) setEditing(false); }
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        placeholder={placeholder}
        className={cn("h-7 text-[11px]", isMissing && !draft && "border-warning/60 bg-warning/5")}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => { setDraft(value); setEditing(true); }}
      className={cn(
        "text-left w-full px-1.5 py-0.5 rounded hover:bg-muted/60 transition-colors text-[11px]",
        isMissing && "text-warning font-medium",
      )}
    >
      {value || placeholder}
    </button>
  );
}

function InlineNumberCell({
  value, placeholder, isMissing, onCommit,
}: { value: number | null; placeholder: string; isMissing: boolean; onCommit: (n: number) => void }) {
  const [editing, setEditing] = useState(isMissing);
  const [draft, setDraft] = useState<string>(isMissing || value == null ? "" : String(value));

  const commit = () => {
    const n = parseFloat(draft);
    if (!isNaN(n) && n > 0) {
      onCommit(n);
      setEditing(false);
    } else if (!isMissing) {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <Input
        autoFocus
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setDraft(v);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setDraft(value == null ? "" : String(value)); setEditing(false); }
        }}
        placeholder={placeholder}
        className={cn("h-7 w-[140px] ml-auto text-[11px] text-right tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none", isMissing && !draft && "border-warning/60 bg-warning/5")}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => { setDraft(value == null ? "" : String(value)); setEditing(true); }}
      className={cn(
        "text-right w-full px-1.5 py-0.5 rounded hover:bg-muted/60 transition-colors text-[11px] tabular-nums",
        isMissing ? "text-warning font-semibold" : "",
      )}
    >
      {value != null && value > 0 ? `$${value.toLocaleString()}` : placeholder}
    </button>
  );
}

/* ────────── VENDOR / PAYMENT PANE ────────── */
function VendorPaymentPane({
  packet, vendorLeadTimes, onUpdate,
}: {
  packet: OrderingPacket;
  vendorLeadTimes: import("@/data/orderingPacket/types").VendorLeadTimeConfig[];
  onUpdate: (patch: Partial<Pick<OrderingPacket, "assignedTo" | "vendorOrderNumber" | "paymentMethod" | "paymentTerms" | "expectedDeliveryDate" | "notes">>) => void;
}) {
  const leadTime = vendorLeadTimes.find((v) => v.vendorName === packet.intendedVendor);
  return (
    <div className="grid grid-cols-2 gap-6 max-w-4xl">
      <div className="space-y-4">
        <h3 className="text-[12px] font-bold uppercase tracking-wider text-foreground flex items-center gap-1.5">
          <Truck className="h-3.5 w-3.5" /> Vendor Confirmation
        </h3>
        <Field label="Vendor Order Number">
          <Input
            value={packet.vendorOrderNumber ?? ""}
            onChange={(e) => onUpdate({ vendorOrderNumber: e.target.value })}
            placeholder="e.g. SEN-987654"
            className="h-9 text-[12px]"
          />
        </Field>
        <Field label="Expected Delivery / Intake Date">
          <Input
            type="date"
            value={packet.expectedDeliveryDate?.slice(0, 10) ?? ""}
            onChange={(e) => onUpdate({ expectedDeliveryDate: e.target.value })}
            className="h-9 text-[12px]"
          />
          {leadTime && (
            <p className="text-[10px] text-muted-foreground mt-1">
              {packet.intendedVendor} default lead time: <span className="font-semibold">{leadTime.leadTimeDays} days</span>
            </p>
          )}
        </Field>
        <Field label="Assigned Ordering Rep">
          <Input
            value={packet.assignedTo ?? ""}
            onChange={(e) => onUpdate({ assignedTo: e.target.value })}
            placeholder="Rep name"
            className="h-9 text-[12px]"
          />
        </Field>
      </div>

      <div className="space-y-4">
        <h3 className="text-[12px] font-bold uppercase tracking-wider text-foreground flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5" /> Payment
        </h3>
        <Field label="Payment Method">
          <Select
            value={packet.paymentMethod ?? ""}
            onValueChange={(v) => onUpdate({ paymentMethod: v as OrderingPacket["paymentMethod"] })}
          >
            <SelectTrigger className="h-9 text-[12px]"><SelectValue placeholder="Select payment method…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Net 30">Net 30</SelectItem>
              <SelectItem value="Net 15">Net 15</SelectItem>
              <SelectItem value="COD">COD</SelectItem>
              <SelectItem value="Credit Card">Credit Card</SelectItem>
              <SelectItem value="ACH">ACH</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Payment Terms (notes)">
          <Textarea
            value={packet.paymentTerms ?? ""}
            onChange={(e) => onUpdate({ paymentTerms: e.target.value })}
            placeholder="e.g. 50% deposit on order, balance on delivery…"
            className="text-[12px] min-h-[80px]"
          />
        </Field>
        <Field label="Internal Notes">
          <Textarea
            value={packet.notes}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            className="text-[12px] min-h-[60px]"
          />
        </Field>
      </div>
    </div>
  );
}

/* ────────── AUDIT PANE ────────── */
function AuditPane({ packet }: { packet: OrderingPacket }) {
  const sorted = [...packet.auditEvents].reverse();
  return (
    <div className="space-y-2">
      <h3 className="text-[12px] font-bold uppercase tracking-wider text-foreground flex items-center gap-1.5">
        <History className="h-3.5 w-3.5" /> Audit Trail
        <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal">
          — every change to this packet, in reverse order
        </span>
      </h3>
      <div className="rounded-lg border border-border divide-y divide-border">
        {sorted.length === 0 ? (
          <div className="p-4 text-center text-[11px] text-muted-foreground">No audit events yet.</div>
        ) : (
          sorted.map((ev) => (
            <div key={ev.id} className="p-3 hover:bg-muted/30">
              <div className="flex items-start gap-3">
                <Badge variant="outline" className="text-[9px] uppercase tracking-wider shrink-0">
                  {ev.kind.replace(/_/g, " ")}
                </Badge>
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-[11px] font-medium text-foreground">{ev.summary}</p>
                  {ev.reason && (
                    <p className="text-[10px] text-muted-foreground italic">Reason: {ev.reason}</p>
                  )}
                  {ev.varianceAmount != null && ev.varianceAmount !== 0 && (
                    <p className={cn("text-[10px] font-semibold tabular-nums",
                      ev.varianceAmount > 0 ? "text-warning" : "text-success")}>
                      Variance: {ev.varianceAmount > 0 ? "+" : ""}${ev.varianceAmount.toLocaleString()}
                    </p>
                  )}
                  <p className="text-[9px] text-muted-foreground">
                    {format(new Date(ev.at), "MMM d, h:mm:ss a")} · {ev.actor}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ────────── FOOTER ────────── */
function PlacementFooter({
  packet, onMarkPlaced, onMarkReceived, onCancel, onClose,
}: {
  packet: OrderingPacket;
  onMarkPlaced: () => void;
  onMarkReceived: () => void;
  onCancel: (reason: string) => void;
  onClose: () => void;
}) {
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const allOrdered = packet.lineItems.every((l) => l.status === "ordered" || l.status === "received");
  const someOrdered = packet.lineItems.some((l) => l.status === "ordered" || l.status === "received");
  const canMarkPlaced = someOrdered && packet.status !== "placed" && packet.status !== "received" && packet.status !== "cancelled";
  const canMarkReceived = packet.status === "placed" || packet.status === "partially_placed";
  const isClosed = packet.status === "received" || packet.status === "cancelled";

  return (
    <div className="border-t border-border px-6 py-3 bg-muted/10">
      {showCancel ? (
        <div className="space-y-2">
          <label className="text-[11px] font-semibold text-destructive">Cancellation reason (required)</label>
          <Textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Why is this packet being cancelled?"
            className="text-[11px] min-h-[60px]"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => { setShowCancel(false); setCancelReason(""); }}>
              Back
            </Button>
            <PermissionGate permission="ordering_approve">
              <Button size="sm" variant="destructive" className="h-8 text-[11px]" disabled={!cancelReason.trim()} onClick={() => { onCancel(cancelReason); setShowCancel(false); setCancelReason(""); }}>
                Confirm Cancel
              </Button>
            </PermissionGate>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] text-muted-foreground flex items-center gap-2">
            {(() => {
              const unrec = packet.lineItems.filter((l) => !l.receiptId).length;
              if (canMarkReceived && unrec > 0) {
                return (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-warning/10 border border-warning/30 text-warning font-semibold">
                    <AlertTriangle className="h-3 w-3" /> {unrec} line{unrec === 1 ? "" : "s"} not on a receipt
                  </span>
                );
              }
              return null;
            })()}
            {packet.placedAt && `Placed ${format(new Date(packet.placedAt), "MMM d, h:mm a")} by ${packet.placedBy}. `}
            {packet.receivedAt && `Received ${format(new Date(packet.receivedAt), "MMM d, h:mm a")}.`}
          </div>
          <div className="flex items-center gap-2">
            {!isClosed && (
              <PermissionGate permission="ordering_approve">
                <Button size="sm" variant="ghost" className="h-8 text-[11px] text-destructive hover:bg-destructive/10" onClick={() => setShowCancel(true)}>
                  <Ban className="h-3 w-3 mr-1" /> Cancel Packet
                </Button>
              </PermissionGate>
            )}
            <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={onClose}>
              <X className="h-3 w-3 mr-1" /> Close
            </Button>
            {canMarkPlaced && (
              <PermissionGate permission="ordering_approve">
                <Button size="sm" className="h-8 text-[11px] gap-1.5" onClick={onMarkPlaced}>
                  <Send className="h-3 w-3" /> {allOrdered ? "Mark Placed" : "Mark Partially Placed"}
                </Button>
              </PermissionGate>
            )}
            {/* Mark Received removed: receipts are recorded in Warehouse Hub upon physical arrival, not at order placement. */}
          </div>
        </div>
      )}
    </div>
  );
}
